export const ErrorCode = {
  InternalError: 'INTERNAL_ERROR',
  Unauthorized: 'UNAUTHORIZED',
  UserNotFound: 'USER_NOT_FOUND',
  UserForbidden: 'USER_FORBIDDEN',
  BusinessNotFound: 'BUSINESS_NOT_FOUND',
  BusinessApiKeyNotFound: 'BUSINESS_API_KEY_NOT_FOUND',
  BusinessSealNotFound: 'BUSINESS_SEAL_NOT_FOUND',
  BusinessApiKeyTypeLimitReached: 'BUSINESS_API_KEY_TYPE_LIMIT_REACHED',
  BusinessApiKeyNameConflict: 'BUSINESS_API_KEY_NAME_CONFLICT',
  BusinessApiKeyUsageForbidden: 'BUSINESS_API_KEY_USAGE_FORBIDDEN',
  BusinessSealApiKeyNotAddable: 'BUSINESS_SEAL_API_KEY_NOT_ADDABLE',
  BusinessSealUserNotAddable: 'BUSINESS_SEAL_USER_NOT_ADDABLE',
  BusinessActivityTypeNotFound: 'BUSINESS_ACTIVITY_TYPE_NOT_FOUND',
  BusinessForbidden: 'BUSINESS_FORBIDDEN',
  BusinessMemberForbidden: 'BUSINESS_MEMBER_FORBIDDEN',
  BusinessInvitationNotFound: 'BUSINESS_INVITATION_NOT_FOUND',
  BusinessDomainNotFound: 'BUSINESS_DOMAIN_NOT_FOUND',
  BusinessDomainInvalid: 'BUSINESS_DOMAIN_INVALID',
  BusinessDomainBanned: 'BUSINESS_DOMAIN_BANNED',
  BusinessDomainAlreadyTaken: 'BUSINESS_DOMAIN_ALREADY_TAKEN',
  GroupNotFound: 'GROUP_NOT_FOUND',
  GroupNameExists: 'GROUP_NAME_EXISTS',
  GroupMemberLimitReached: 'GROUP_MEMBER_LIMIT_REACHED',
  GroupLimitReached: 'GROUP_LIMIT_REACHED',
  GroupPricePlanNotSupported: 'GROUP_PRICE_PLAN_NOT_SUPPORTED',
  VisualSignatureNotFound: 'VISUAL_SIGNATURE_NOT_FOUND',
  VisualSignatureImageNotFound: 'VISUAL_SIGNATURE_IMAGE_NOT_FOUND',
  SubscriptionSeatsBelowUsed: 'SUBSCRIPTION_SEATS_BELOW_USED',
} as const

export type ErrorCodeType = (typeof ErrorCode)[keyof typeof ErrorCode]

// `satisfies` enforces an entry for every code — adding a code without defaults fails the build
const errorCodeDefaults = {
  [ErrorCode.InternalError]: { status: 500, message: 'Internal server error' },
  [ErrorCode.Unauthorized]: { status: 401, message: 'Unauthorized' },
  [ErrorCode.UserNotFound]: { status: 404, message: 'User not found' },
  [ErrorCode.UserForbidden]: { status: 403, message: 'User is not authorized for this action' },
  [ErrorCode.BusinessNotFound]: { status: 404, message: 'Business not found' },
  [ErrorCode.BusinessApiKeyNotFound]: { status: 404, message: 'Business api-key not found' },
  [ErrorCode.BusinessSealNotFound]: { status: 404, message: 'Business seal not found' },
  [ErrorCode.BusinessApiKeyTypeLimitReached]: { status: 400, message: 'Business api-key type limit reached' },
  [ErrorCode.BusinessApiKeyNameConflict]: { status: 409, message: 'Business api-key name is already taken' },
  [ErrorCode.BusinessApiKeyUsageForbidden]: {
    status: 403,
    message: 'Business api-key usage is not allowed for this business',
  },
  [ErrorCode.BusinessSealApiKeyNotAddable]: {
    status: 400,
    message: 'One or more api-keys can not be added to the seal',
  },
  [ErrorCode.BusinessSealUserNotAddable]: { status: 400, message: 'One or more users can not be added to the seal' },
  [ErrorCode.BusinessActivityTypeNotFound]: { status: 404, message: 'Business activity type not found' },
  [ErrorCode.BusinessForbidden]: { status: 403, message: 'User is not authorized for this business action' },
  [ErrorCode.BusinessMemberForbidden]: { status: 403, message: 'User is not authorized to manage members' },
  [ErrorCode.BusinessInvitationNotFound]: { status: 404, message: 'Business invitation not found' },
  [ErrorCode.BusinessDomainNotFound]: { status: 404, message: 'Business domain not found' },
  [ErrorCode.BusinessDomainInvalid]: { status: 400, message: 'Invalid domain format' },
  [ErrorCode.BusinessDomainBanned]: { status: 400, message: 'Domain is banned or public' },
  [ErrorCode.BusinessDomainAlreadyTaken]: { status: 409, message: 'Domain is already taken' },
  [ErrorCode.GroupNotFound]: { status: 404, message: 'Group not found' },
  [ErrorCode.GroupNameExists]: { status: 400, message: 'Group name already exists' },
  [ErrorCode.GroupMemberLimitReached]: { status: 400, message: 'Group member limit reached' },
  [ErrorCode.GroupLimitReached]: { status: 400, message: 'Group limit reached' },
  [ErrorCode.GroupPricePlanNotSupported]: { status: 400, message: 'Price plan does not support groups' },
  [ErrorCode.VisualSignatureNotFound]: { status: 404, message: 'Visual signature not found' },
  [ErrorCode.VisualSignatureImageNotFound]: { status: 404, message: 'Visual signature image not found' },
  [ErrorCode.SubscriptionSeatsBelowUsed]: {
    status: 400,
    message: 'Requested seats are fewer than the Full seats in use',
  },
} satisfies Record<ErrorCodeType, { status: number; message: string }>

export function mapErrorCodeToStatus(errorCode: ErrorCodeType): number {
  return errorCodeDefaults[errorCode].status
}

export function mapErrorCodeToMessage(errorCode: ErrorCodeType): string {
  return errorCodeDefaults[errorCode].message
}

const errorCodeValues = new Set<string>(Object.values(ErrorCode))

/**
 * Extracts the domain error code from an error of unknown shape.
 *
 * Works for both sides of the wire: a server-side H3Error carries the code at `error.data.code`,
 * while a client-side FetchError wraps the serialized H3 payload, putting it at `error.data.data.code`.
 */
export function getErrorCode(error: unknown): ErrorCodeType | undefined {
  if (!error || typeof error !== 'object') {
    return undefined
  }
  const data = (error as { data?: { code?: unknown; data?: { code?: unknown } } }).data
  const code = data?.code ?? data?.data?.code
  return typeof code === 'string' && errorCodeValues.has(code) ? (code as ErrorCodeType) : undefined
}
